import { useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { SearchableSelect } from '@/components/ui/searchable-select';
import {
  Rows, Trash, X, Plugs, PencilSimple, ArrowSquareOut, Camera,
  Laptop, Monitor, DeviceMobile, Printer, WifiHigh, HardDrives, Database, Keyboard, Package,
} from '@phosphor-icons/react';
import { itApi, fileUrl, OWNERSHIP_META } from './itApi';
import { formatDateTime } from '@/utils/datetime';

const ROW_H = 26;       // 1U の高さ(px)
const CHANNEL_W = 96;   // 右側の配線チャネル幅(px)

const SIDES = [['front', '前面'], ['rear', '背面']];

const CABLE_TYPES = {
  fiber: { label: '光ファイバー', color: '#fb7185', dash: '8 4 2 4' },
  lan: { label: 'LANケーブル CAT6', color: '#fbbf24', dash: '6 4' },
  other: { label: 'その他', color: '#a1a1aa', dash: '' },
};
const CABLE_DOTS = [['fiber', '#e11d48', '光'], ['lan', '#ca8a04', 'LAN'], ['other', '#71717a', '他']];

const typeFor = (category) => {
  const c = category || '';
  if (/ネットワーク|ルーター|Wi-?Fi|スイッチ/i.test(c)) return 'network';
  if (/サーバー/.test(c)) return 'server';
  if (/NAS|ストレージ/.test(c)) return 'storage';
  if (/PC|パソコン|ノート/.test(c)) return 'pc';
  if (/モニター|ディスプレイ/.test(c)) return 'monitor';
  if (/プリンター|複合機/.test(c)) return 'printer';
  if (/スマート|タブレット|携帯|電話/.test(c)) return 'phone';
  if (/周辺機器|キーボード|マウス/.test(c)) return 'peripheral';
  return 'default';
};

const TYPE_STYLE = {
  network: { Icon: WifiHigh, cls: 'from-sky-700/90 to-sky-600/90 border-sky-400/50' },
  server: { Icon: HardDrives, cls: 'from-violet-700/90 to-violet-600/90 border-violet-400/50' },
  storage: { Icon: Database, cls: 'from-amber-700/90 to-amber-600/90 border-amber-400/50' },
  pc: { Icon: Laptop, cls: 'from-blue-700/90 to-blue-600/90 border-blue-400/50' },
  monitor: { Icon: Monitor, cls: 'from-cyan-700/90 to-cyan-600/90 border-cyan-400/50' },
  printer: { Icon: Printer, cls: 'from-teal-700/90 to-teal-600/90 border-teal-400/50' },
  phone: { Icon: DeviceMobile, cls: 'from-emerald-700/90 to-emerald-600/90 border-emerald-400/50' },
  peripheral: { Icon: Keyboard, cls: 'from-zinc-600/90 to-zinc-500/90 border-zinc-400/50' },
  default: { Icon: Package, cls: 'from-zinc-700/90 to-zinc-600/90 border-zinc-400/50' },
};

// ラックビュー — U位置エレベーション + ラック内配線。
export default function RackDialog({ open, onClose, rack, assets, allAssets, canEdit, onChanged, onStartConnect, conns = [] }) {
  const [side, setSide] = useState('front');
  const [addId, setAddId] = useState('');
  const [addUnit, setAddUnit] = useState('1');
  const [addSize, setAddSize] = useState('1');
  const [editName, setEditName] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showWires, setShowWires] = useState(true);
  const [hoverWire, setHoverWire] = useState(null);
  const [hoverDev, setHoverDev] = useState(null);
  const [connectFrom, setConnectFrom] = useState(null); // {id, type}
  const [editWire, setEditWire] = useState(null);       // {id, label}
  const [photoOpen, setPhotoOpen] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const photoInputRef = useRef(null);

  const mounted = useMemo(() => (assets || []).filter((a) => a.rack_id === rack?.id), [assets, rack]);
  const mountedIds = useMemo(() => new Set(mounted.map((a) => a.id)), [mounted]);
  const sideMounted = mounted.filter((a) => (a.rack_side || 'front') === side);
  const devById = useMemo(() => new Map(mounted.map((a) => [a.id, a])), [mounted]);

  // ラック内配線 — 両端がこのラックに搭載されている接続
  const internalConns = useMemo(
    () => conns.filter((c) => mountedIds.has(c.from_asset_id) && mountedIds.has(c.to_asset_id)),
    [conns, mountedIds],
  );
  const sideConns = internalConns.filter((c) => {
    const f = devById.get(c.from_asset_id); const t = devById.get(c.to_asset_id);
    return (f?.rack_side || 'front') === side && (t?.rack_side || 'front') === side;
  });

  if (!rack) return null;
  const units = rack.units || 42;
  const usedU = (() => {
    const occ = new Set();
    mounted.forEach((a) => {
      const start = a.rack_unit || 1;
      for (let i = 0; i < (a.rack_size || 1); i += 1) occ.add(start + i);
    });
    return occ.size;
  })();
  const freeU = Math.max(0, units - usedU);
  const photo = (rack.photos || {})[side];

  const onPhotoFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setPhotoBusy(true);
    try {
      await itApi.uploadRackPhoto(rack.id, side, file);
      toast.success(`実機写真（${side === 'front' ? '前面' : '背面'}）を保存しました`);
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || '写真のアップロードに失敗しました');
    } finally { setPhotoBusy(false); }
  };

  const delPhoto = async () => {
    if (!window.confirm(`実機写真（${side === 'front' ? '前面' : '背面'}）を削除しますか？`)) return;
    try {
      await itApi.deleteRackPhoto(rack.id, side);
      toast.success('実機写真を削除しました');
      setPhotoOpen(false);
      onChanged();
    } catch { toast.error('削除に失敗しました'); }
  };

  const devTop = (a) => (units - ((a.rack_unit || 1) + (a.rack_size || 1) - 1)) * ROW_H;
  const devMidY = (a) => devTop(a) + ((a.rack_size || 1) * ROW_H) / 2;

  const mount = async () => {
    if (!addId) { toast.error('搭載する資産を選択してください'); return; }
    setBusy(true);
    try {
      await itApi.setAssetRack(addId, {
        rack_id: rack.id, rack_unit: Number(addUnit) || 1,
        rack_size: Number(addSize) || 1, rack_side: side,
      });
      toast.success('ラックに搭載しました');
      setAddId(''); setAddUnit('1'); setAddSize('1');
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '搭載に失敗しました');
    } finally { setBusy(false); }
  };

  const unmount = async (aid) => {
    try {
      await itApi.setAssetRack(aid, { rack_id: null });
      toast.success('ラックから外しました');
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || '取り外しに失敗しました'); }
  };

  const saveName = async () => {
    if (!editName?.trim()) { setEditName(null); return; }
    try {
      await itApi.updateRack(rack.id, { name: editName.trim() });
      toast.success('ラック名を変更しました'); setEditName(null); onChanged();
    } catch { toast.error('変更に失敗しました'); }
  };

  const delRack = async () => {
    if (!window.confirm(`ラック「${rack.name}」を削除しますか？搭載中の機器はラックから外れます。`)) return;
    try {
      await itApi.deleteRack(rack.id);
      toast.success('ラックを削除しました'); onClose(); onChanged();
    } catch { toast.error('削除に失敗しました'); }
  };

  const onDeviceClick = async (a) => {
    if (!connectFrom) return;
    if (a.id === connectFrom.id) { toast.error('同じ機器同士は接続できません'); return; }
    try {
      await itApi.createFloormapConnection(rack.floormap_id, {
        from_asset_id: connectFrom.id, to_asset_id: a.id, cable_type: connectFrom.type,
      });
      toast.success('ラック内配線を追加しました');
      setConnectFrom(null);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || '配線の追加に失敗しました'); }
  };

  const deleteWire = async (cid) => {
    try {
      await itApi.deleteFloormapConnection(rack.floormap_id, cid);
      toast.success('配線を削除しました'); onChanged();
    } catch { toast.error('削除に失敗しました'); }
  };

  const saveWireLabel = async () => {
    if (!editWire) return;
    try {
      await itApi.updateFloormapConnection(rack.floormap_id, editWire.id, { label: editWire.label });
      toast.success('メモを保存しました'); setEditWire(null); onChanged();
    } catch { toast.error('保存に失敗しました'); }
  };

  const wireActive = (c) => hoverWire === c.id
    || (hoverDev && (c.from_asset_id === hoverDev || c.to_asset_id === hoverDev));
  const anyFocus = hoverWire || hoverDev;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="it-rack-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Rows size={18} weight="duotone" className="text-zinc-700" />
            {editName === null ? (
              <span className="flex items-center gap-2">
                {rack.name}
                <Badge variant="outline" className="text-[10px] font-mono">{units}U</Badge>
                <Badge variant="outline" className={`text-[10px] font-mono ${
                  freeU === 0 ? 'bg-rose-50 text-rose-700 border-rose-200'
                    : usedU / units >= 0.7 ? 'bg-amber-50 text-amber-700 border-amber-200'
                      : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                }`} data-testid="it-rack-free-badge">空き{freeU}U</Badge>
                {canEdit && (
                  <button type="button" className="text-[10px] text-zinc-400 hover:text-teal-700 font-normal"
                    onClick={() => setEditName(rack.name)} data-testid="it-rack-rename">名称変更</button>
                )}
              </span>
            ) : (
              <span className="flex items-center gap-1">
                <Input value={editName} onChange={(e) => setEditName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && saveName()} className="h-7 text-sm w-44" autoFocus
                  data-testid="it-rack-name-input" />
                <Button size="sm" className="h-7 px-2 text-xs" onClick={saveName} data-testid="it-rack-name-save">保存</Button>
              </span>
            )}
            {canEdit && (
              <button type="button" className="ml-auto mr-4 text-zinc-400 hover:text-rose-500" title="ラックを削除"
                onClick={delRack} data-testid="it-rack-delete">
                <Trash size={14} />
              </button>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex items-center gap-0.5 rounded-lg bg-zinc-100 p-0.5" data-testid="it-rack-side-tabs">
            {SIDES.map(([k, l]) => (
              <button key={k} type="button" onClick={() => { setSide(k); setConnectFrom(null); }}
                data-testid={`it-rack-side-${k}`}
                className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors ${
                  side === k ? 'bg-white text-teal-700 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'
                }`}>
                {l} ({mounted.filter((a) => (a.rack_side || 'front') === k).length})
              </button>
            ))}
          </div>
          <button type="button" onClick={() => setShowWires((v) => !v)}
            data-testid="it-rack-wires-toggle"
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold border transition-colors ${
              showWires ? 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-zinc-50 border-zinc-200 text-zinc-400'
            }`}>
            <Plugs size={12} weight="bold" /> 配線 {sideConns.length}
          </button>
          <input ref={photoInputRef} type="file" accept="image/*" className="hidden"
            onChange={onPhotoFile} data-testid="it-rack-photo-input" />
          {photo ? (
            <button type="button" onClick={() => setPhotoOpen(true)} data-testid="it-rack-photo-chip"
              className="inline-flex items-center gap-1.5 pl-1 pr-2.5 py-0.5 rounded-md text-[11px] font-semibold border border-zinc-200 bg-white text-zinc-600 hover:border-zinc-400 transition-colors">
              <img src={fileUrl(photo.url)} alt="実機写真" className="w-6 h-6 rounded object-cover" />
              実機写真
            </button>
          ) : canEdit ? (
            <button type="button" onClick={() => photoInputRef.current?.click()} disabled={photoBusy}
              data-testid="it-rack-photo-add"
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold border border-dashed border-zinc-300 text-zinc-400 hover:text-zinc-600 hover:border-zinc-400 transition-colors disabled:opacity-50">
              <Camera size={12} weight="bold" /> {photoBusy ? 'アップロード中…' : '実機写真を追加'}
            </button>
          ) : null}
        </div>

        {connectFrom && (
          <div className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-[11px] font-semibold text-amber-800" data-testid="it-rack-connect-banner">
            <Plugs size={13} weight="duotone" className="shrink-0" />
            「{devById.get(connectFrom.id)?.name}」から {CABLE_TYPES[connectFrom.type]?.label || ''} — 接続先の機器をクリック
            <button type="button" className="ml-auto inline-flex items-center gap-0.5 text-teal-700"
              onClick={() => { onStartConnect(connectFrom.id, connectFrom.type); setConnectFrom(null); onClose(); }}
              data-testid="it-rack-connect-to-map">
              <ArrowSquareOut size={11} weight="bold" /> マップの機器へ
            </button>
            <button type="button" className="text-zinc-500" onClick={() => setConnectFrom(null)} data-testid="it-rack-connect-cancel">キャンセル</button>
          </div>
        )}

        {/* エレベーション図 — レール + 配線チャネル */}
        <div className="rounded-lg border border-zinc-700 bg-teal-700 p-2 shadow-inner" data-testid="it-rack-elevation">
          <div className="relative" style={{ height: units * ROW_H }}>
            {/* 左レール (取付穴) */}
            <div className="absolute left-7 top-0 bottom-0 w-2 rounded-sm bg-zinc-800"
              style={{ backgroundImage: 'radial-gradient(circle, #52525b 1.4px, transparent 1.5px)', backgroundSize: `100% ${ROW_H / 3}px`, backgroundPosition: 'center 4px' }} />
            {/* 右レール */}
            <div className="absolute top-0 bottom-0 w-2 rounded-sm bg-zinc-800" style={{ right: CHANNEL_W + 4, backgroundImage: 'radial-gradient(circle, #52525b 1.4px, transparent 1.5px)', backgroundSize: `100% ${ROW_H / 3}px`, backgroundPosition: 'center 4px' }} />
            {Array.from({ length: units }, (_, i) => {
              const u = units - i;
              return (
                <div key={u} className="absolute left-0 flex items-center" style={{ top: i * ROW_H, height: ROW_H, right: CHANNEL_W }}>
                  <span className="w-6 shrink-0 text-right pr-1 text-[9px] font-mono text-zinc-600 select-none">{u}</span>
                  <div className="flex-1 h-full border-t border-zinc-800/70 ml-4" />
                </div>
              );
            })}

            {/* デバイスブロック */}
            {sideMounted.map((a) => {
              const start = a.rack_unit || 1;
              const size = a.rack_size || 1;
              const { Icon, cls } = TYPE_STYLE[typeFor(a.category)];
              const om = OWNERSHIP_META[a.ownership];
              const focus = hoverDev === a.id || (connectFrom && connectFrom.id !== a.id);
              const dim = anyFocus && !focus && hoverDev !== a.id && !wireActiveForDev(a.id);
              function wireActiveForDev(id) {
                return hoverWire && sideConns.some((c) => c.id === hoverWire && (c.from_asset_id === id || c.to_asset_id === id));
              }
              return (
                <div key={a.id}
                  className={`absolute left-10 rounded-lg bg-gradient-to-r border shadow-lg px-2 flex items-center gap-1.5 overflow-hidden transition-all duration-200 ${cls} ${
                    connectFrom && connectFrom.id !== a.id ? 'cursor-pointer ring-2 ring-amber-400/80 animate-pulse' : ''
                  } ${dim ? 'opacity-40' : ''} ${hoverDev === a.id ? 'ring-2 ring-white/60 brightness-110' : ''}`}
                  style={{ top: devTop(a) + 1, height: size * ROW_H - 2, right: CHANNEL_W + 8 }}
                  onMouseEnter={() => setHoverDev(a.id)}
                  onMouseLeave={() => setHoverDev(null)}
                  onClick={() => onDeviceClick(a)}
                  data-testid={`it-rack-device-${a.id}`}>
                  <Icon size={13} weight="duotone" className="text-white/80 shrink-0" />
                  <span className="min-w-0 flex-1 truncate">
                    <span className="text-[11px] font-bold text-white">{a.name}</span>
                    <span className="ml-1.5 text-[9px] font-mono text-white/60">
                      {a.asset_tag || ''} U{start}{size > 1 ? `–${start + size - 1}` : ''}
                    </span>
                  </span>
                  {om && a.ownership !== 'owned' && (
                    <Badge variant="outline" className={`text-[8px] px-1 py-0 shrink-0 ${om.cls}`} data-testid={`it-rack-ownership-${a.id}`}>
                      {om.label}
                    </Badge>
                  )}
                  {canEdit && !connectFrom && (
                    <span className="flex items-center gap-1 shrink-0">
                      {CABLE_DOTS.map(([key, color, l]) => (
                        <button key={key} type="button" title={`${l}ケーブルで配線を開始`}
                          onClick={(e) => { e.stopPropagation(); setConnectFrom({ id: a.id, type: key }); }}
                          data-testid={`it-rack-connect-${key}-${a.id}`}
                          className="w-3.5 h-3.5 rounded-full ring-1 ring-white/50 hover:scale-125 transition-transform"
                          style={{ background: color }} />
                      ))}
                      <button type="button" className="text-white/60 hover:text-white" title="ラックから外す"
                        onClick={(e) => { e.stopPropagation(); unmount(a.id); }} data-testid={`it-rack-unmount-${a.id}`}>
                        <X size={12} />
                      </button>
                    </span>
                  )}
                </div>
              );
            })}

            {/* ラック内配線 (右チャネル) */}
            {showWires && sideConns.length > 0 && (
              <svg className="absolute top-0 bottom-0 right-0 h-full" width={CHANNEL_W} data-testid="it-rack-wires">
                {sideConns.map((c, i) => {
                  const f = devById.get(c.from_asset_id);
                  const t = devById.get(c.to_asset_id);
                  const ct = CABLE_TYPES[c.cable_type] || CABLE_TYPES.other;
                  const y1 = devMidY(f); const y2 = devMidY(t);
                  const laneX = 14 + (i % 7) * 11;
                  const active = wireActive(c);
                  return (
                    <g key={c.id}>
                      <path d={`M 0 ${y1} L ${laneX} ${y1} L ${laneX} ${y2} L 0 ${y2}`} fill="none"
                        stroke={ct.color} strokeWidth={active ? 3 : 1.8}
                        strokeDasharray={ct.dash || undefined} strokeLinecap="round" strokeLinejoin="round"
                        opacity={anyFocus && !active ? 0.15 : 0.95}
                        className="transition-all duration-150"
                        data-testid={`it-rack-wire-${c.id}`} />
                      <circle cx={2} cy={y1} r={active ? 3.5 : 2.5} fill={ct.color} opacity={anyFocus && !active ? 0.15 : 1} />
                      <circle cx={2} cy={y2} r={active ? 3.5 : 2.5} fill={ct.color} opacity={anyFocus && !active ? 0.15 : 1} />
                      <path d={`M 0 ${y1} L ${laneX} ${y1} L ${laneX} ${y2} L 0 ${y2}`} fill="none" stroke="transparent"
                        strokeWidth={12} style={{ pointerEvents: 'stroke', cursor: 'pointer' }}
                        onMouseEnter={() => setHoverWire(c.id)} onMouseLeave={() => setHoverWire(null)} />
                    </g>
                  );
                })}
              </svg>
            )}

            {/* ホバー中の配線ラベル */}
            {showWires && hoverWire && (() => {
              const c = sideConns.find((x) => x.id === hoverWire);
              if (!c) return null;
              const ct = CABLE_TYPES[c.cable_type] || CABLE_TYPES.other;
              const midY = (devMidY(devById.get(c.from_asset_id)) + devMidY(devById.get(c.to_asset_id))) / 2;
              return (
                <div className="absolute pointer-events-none rounded-md bg-zinc-800/95 border border-zinc-600 px-2 py-1 shadow-xl"
                  style={{ right: CHANNEL_W + 12, top: midY - 14 }} data-testid="it-rack-wire-tooltip">
                  <p className="text-[9px] font-bold whitespace-nowrap" style={{ color: ct.color }}>{ct.label}</p>
                  {c.label && <p className="text-[9px] text-zinc-300 font-mono whitespace-nowrap">{c.label}</p>}
                </div>
              );
            })()}
          </div>
        </div>

        {/* 配線リスト */}
        {internalConns.length > 0 && (
          <div className="space-y-1" data-testid="it-rack-wire-list">
            <p className="text-[10px] font-semibold text-zinc-500 flex items-center gap-1"><Plugs size={11} weight="bold" /> ラック内配線 ({internalConns.length})</p>
            {internalConns.map((c) => {
              const f = devById.get(c.from_asset_id); const t = devById.get(c.to_asset_id);
              const ct = CABLE_TYPES[c.cable_type] || CABLE_TYPES.other;
              const crossSide = (f?.rack_side || 'front') !== (t?.rack_side || 'front');
              return (
                <div key={c.id}
                  className={`rounded-lg border px-2 py-1.5 text-[11px] transition-colors ${hoverWire === c.id ? 'border-amber-300 bg-amber-50/50' : 'border-zinc-100 bg-white'}`}
                  onMouseEnter={() => setHoverWire(c.id)} onMouseLeave={() => setHoverWire(null)}
                  data-testid={`it-rack-wire-row-${c.id}`}>
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: ct.color }} />
                    <span className="font-semibold text-zinc-700 truncate">{f?.name}</span>
                    <span className="text-zinc-400">—</span>
                    <span className="font-semibold text-zinc-700 truncate">{t?.name}</span>
                    {crossSide && <Badge variant="outline" className="text-[8px] px-1 py-0 shrink-0">前↔背</Badge>}
                    {c.label && <span className="font-mono text-[9px] text-zinc-400 truncate" data-testid={`it-rack-wire-label-${c.id}`}>{c.label}</span>}
                    {canEdit && (
                      <span className="ml-auto flex items-center gap-1 shrink-0">
                        <button type="button" className="text-zinc-400 hover:text-teal-700" title="メモを編集"
                          onClick={() => setEditWire(editWire?.id === c.id ? null : { id: c.id, label: c.label || '' })}
                          data-testid={`it-rack-wire-edit-${c.id}`}>
                          <PencilSimple size={11} />
                        </button>
                        <button type="button" className="text-zinc-400 hover:text-rose-500" title="配線を削除"
                          onClick={() => deleteWire(c.id)} data-testid={`it-rack-wire-delete-${c.id}`}>
                          <X size={12} />
                        </button>
                      </span>
                    )}
                  </div>
                  {editWire?.id === c.id && (
                    <div className="flex gap-1 mt-1">
                      <Input value={editWire.label} onChange={(e) => setEditWire({ ...editWire, label: e.target.value })}
                        onKeyDown={(e) => e.key === 'Enter' && saveWireLabel()}
                        placeholder="ポート/VLAN等のメモ" className="h-6 text-[10px]" autoFocus
                        data-testid={`it-rack-wire-label-input-${c.id}`} />
                      <Button size="sm" className="h-6 px-2 text-[10px] bg-teal-700 hover:bg-teal-800" onClick={saveWireLabel} data-testid={`it-rack-wire-label-save-${c.id}`}>保存</Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {canEdit && (
          <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-2.5 space-y-2" data-testid="it-rack-add-form">
            <p className="text-[10px] font-semibold text-zinc-500">機器を搭載（{side === 'front' ? '前面' : '背面'}）</p>
            <SearchableSelect
              value={addId} onValueChange={setAddId}
              placeholder="資産を選択…" searchPlaceholder="品名・資産番号で検索"
              testid="it-rack-add-asset"
              options={(allAssets || []).filter((a) => a.rack_id !== rack.id).map((a) => ({
                value: a.id, primary: a.name,
                secondary: `${a.asset_tag || ''}${a.rack_id ? ' (他ラック搭載中)' : a.floormap_id ? ' (マップ配置中)' : ''}`,
              }))}
            />
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-zinc-500">開始U</span>
              <Input type="number" min={1} max={units} value={addUnit} onChange={(e) => setAddUnit(e.target.value)}
                className="h-8 w-16 text-xs" data-testid="it-rack-add-unit" />
              <span className="text-[10px] text-zinc-500">高さ</span>
              <Input type="number" min={1} max={12} value={addSize} onChange={(e) => setAddSize(e.target.value)}
                className="h-8 w-14 text-xs" data-testid="it-rack-add-size" />
              <span className="text-[10px] text-zinc-500">U</span>
              <Button size="sm" className="h-8 px-3 text-xs ml-auto bg-teal-700 hover:bg-teal-800"
                disabled={busy} onClick={mount} data-testid="it-rack-add-submit">
                搭載する
              </Button>
            </div>
          </div>
        )}

        <p className="text-[10px] text-zinc-400 flex items-center gap-1">
          <Plugs size={11} /> 機器の色付きの点をクリック → 別の機器をクリックでラック内配線。「マップの機器へ」でラック外接続もできます
        </p>

        {/* 実機写真プレビュー */}
        <Dialog open={photoOpen} onOpenChange={(v) => !v && setPhotoOpen(false)}>
          <DialogContent className="max-w-lg" data-testid="it-rack-photo-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-base">
                <Camera size={16} weight="duotone" className="text-zinc-600" />
                実機写真 — {rack.name}（{side === 'front' ? '前面' : '背面'}）
              </DialogTitle>
            </DialogHeader>
            {photo && (
              <>
                <img src={fileUrl(photo.url)} alt={`${rack.name} 実機写真`}
                  className="w-full max-h-[60vh] object-contain rounded-lg border border-zinc-200 bg-zinc-50"
                  data-testid="it-rack-photo-full" />
                <div className="flex items-center gap-2">
                  <p className="text-[10px] text-zinc-400">
                    {photo.uploaded_by}{photo.uploaded_at ? ` ・ ${formatDateTime(photo.uploaded_at)}` : ''}
                  </p>
                  {canEdit && (
                    <span className="ml-auto flex items-center gap-2">
                      <Button size="sm" variant="outline" className="h-7 px-2.5 text-xs" disabled={photoBusy}
                        onClick={() => photoInputRef.current?.click()} data-testid="it-rack-photo-replace">
                        <Camera size={13} className="mr-1" /> 差し替え
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 px-2.5 text-xs text-rose-600 border-rose-200 hover:bg-rose-50"
                        onClick={delPhoto} data-testid="it-rack-photo-delete">
                        <Trash size={13} className="mr-1" /> 削除
                      </Button>
                    </span>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
